import { ChangeDetectorRef, Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import * as moment from 'moment';
import { CovanceFilterDateRangeType } from './../../covance-filter/filter-type/covance-filter-date-range-type';
import { IMyDpOptions, IMyDateModel, IMyDate } from 'mydatepicker';
import { Event } from '@angular/router';

export type DateRangeType = 'dateRange' | 'today' | 'weekly' | 'monthly' | 'twoMonths' | 'quarterly';

export interface DateRangeFilterValue {
    rangeType: DateRangeType;
    dateBegin: string;
    dateEnd: string;
}

export interface RangeKind {
    key: DateRangeType;
    value: string;
    selected: boolean;
}

@Component({
    selector: 'timeframe-filter',
    template: require('./timeframe-filter.component.html'),
    styles: [require('./timeframe-filter.component.scss')]
})
export class TimeframeFilterComponent implements OnChanges {

    @Input()
    public label: string;

    @Input()
    public previousDateRangeValue: DateRangeFilterValue;

    @Input()
    public timeFrameRanges: RangeKind[] = [];

    @Input()
    public disableToDate = false;

    @Input()
    public customFromDateLabel = 'DD/MM';

    @Input()
    public customToDateLabel = 'DD/MM';

    @Input()
    public myDatePickerOptions: IMyDpOptions;

    @Output()
    public valueChange = new EventEmitter<DateRangeFilterValue>();

    public selectedRange: RangeKind;

    public dateRangeValue: DateRangeFilterValue;

    public isShowToSelector: boolean;

    public isShowFromSelector: boolean;

    public showDropDown = false;

    public fromDate: IMyDate;

    public toDate: IMyDate;

    public customRangeKind: RangeKind;

    public rangeKinds: RangeKind[] = [
        { key: 'today', value: 'Today', selected: false },
        { key: 'weekly', value: 'Last 7 days', selected: false },
        { key: 'monthly', value: 'Last 30 days', selected: true },
        { key: 'twoMonths', value: 'Last 60 days', selected: false },
        { key: 'quarterly', value: 'Last 90 days', selected: false },
    ];

    public isDateChanged = false;

    public isDayChanged = false;

    public isMonthChanged = false;

    public isYearChanged = false;

    public isKeyDown = false;

    public isSelectionExists = false;

    public isMonthFormateManipulated = false;

    public keyDownValue = '';

    public currCursorPosition = 0;

    public get isDateRangeKindSelected(): boolean {
        return this.selectedRange != null && this.selectedRange.key === 'dateRange';
    }

    constructor(protected filterDateRangeType: CovanceFilterDateRangeType, private ref: ChangeDetectorRef) {
        if (this.timeFrameRanges.length === 0) {
            this.timeFrameRanges = this.rangeKinds;
        }
        if (!this.myDatePickerOptions) {
            this.myDatePickerOptions = {
                dateFormat: 'dd-mmm-yyyy',
                showTodayBtn: true,
                todayBtnTxt: 'Today'
            };
        }
        let label = this.customFromDateLabel + ' - ' + this.customToDateLabel;
        this.customRangeKind = {
            key: 'dateRange', value: label, selected: false
        };
        let defaultDate: Date = new Date();
        this.fromDate = this.loadDefaultDates(defaultDate);
        this.toDate = this.loadDefaultDates(defaultDate);
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.customFromDateLabel) {
            this.customRangeKind.value = this.customFromDateLabel + ' - ' + this.customToDateLabel;
        }
        if (changes.customToDateLabel) {
            this.customRangeKind.value = this.customFromDateLabel + ' - ' + this.customToDateLabel;
        }
        if (changes.previousDateRangeValue) {
            if (!this.previousDateRangeValue) { return; }
            if (this.previousDateRangeValue.rangeType === 'dateRange') {
                if (!this.dateRangeValue || (this.dateRangeValue.dateBegin !== this.previousDateRangeValue.dateBegin ||
                    this.dateRangeValue.dateEnd !== this.previousDateRangeValue.dateEnd)) {
                    let event: Event;
                    this.fromDate = this.loadDefaultDates(new Date(this.previousDateRangeValue.dateBegin));
                    this.toDate = this.loadDefaultDates(new Date(this.previousDateRangeValue.dateEnd));
                    this.togglePopover(event, false);
                    this.onPopoverHide();
                }
            }
            if (this.previousDateRangeValue.rangeType !== 'dateRange') {
                let range = this.timeFrameRanges.filter(t => t.key === this.previousDateRangeValue.rangeType)[0];
                this.onRangeKindChange(range);
            }
        }
    }

    public loadDefaultDates(defaultDate: Date) {
        return {
            year: defaultDate.getFullYear(),
            month: defaultDate.getMonth() + 1,
            day: defaultDate.getDate()
        };
    }

    public onRangeKindChange(selectedRangeKind: RangeKind) {
        this.handleTimeframeFilter(selectedRangeKind);
        this.selectedRange = selectedRangeKind;
        this.onPopoverHide();
        switch (selectedRangeKind.key) {
            case 'today':
                this.dateRangeValue = null;
                this.valueChange.emit(this.getTodayRange());
                break;
            case 'weekly':
                this.dateRangeValue = null;
                this.valueChange.emit(this.getWeeklyRange());
                break;
            case 'monthly':
                this.dateRangeValue = null;
                this.valueChange.emit(this.getMonthlyRange());
                break;
            case 'twoMonths':
                this.dateRangeValue = null;
                this.valueChange.emit(this.getTwoMonthsRange());
                break;
            case 'quarterly':
                this.dateRangeValue = null;
                this.valueChange.emit(this.getQuarterlyRange());
                break;
        }
    }

    public applyDateChanges() {
        if (this.isInvalidDateRange() || !this.dateRangeValue.dateBegin || !this.dateRangeValue.dateEnd) { return; }
        this.onPopoverHide();
        this.valueChange.emit(this.dateRangeValue);
    }

    public togglePopover(event: Event, triggerEmit = true) {
        this.onRangeKindChange(this.customRangeKind);
        const newRangeValue = this.getDateRangeType();
        this.dateRangeValue = this.dateRangeValue ? this.dateRangeValue : this.getEmptyDateRangeValue();
        if ((newRangeValue.dateBegin !== this.dateRangeValue.dateBegin ||
            newRangeValue.dateEnd !== this.dateRangeValue.dateEnd) && triggerEmit) {
            this.valueChange.emit(newRangeValue);
        }
        this.dateRangeValue = newRangeValue;
        this.showDropDown = !this.showDropDown;
    }

    public handleTimeframeFilter(selectedRange: RangeKind) {
        for (let timeRange of this.timeFrameRanges) {
            timeRange.selected = false;
        }
        selectedRange.selected = true;
        if (selectedRange.key !== 'dateRange') {
            this.customRangeKind.selected = false;
        }
    }

    public handleNgClass(selectedRange: RangeKind) {
        return selectedRange.selected ? 'timeFrameButton-active' : 'timeFrameButton-standard';
    }

    public onShowToSelector(isShowToSelector: boolean) {
        this.isShowToSelector = isShowToSelector;
    }

    public onShowFromSelector(isShowFromSelector: boolean) {
        this.isShowFromSelector = isShowFromSelector;
    }

    public onDateBeginChange($event: IMyDateModel) {
        this.fromDate = $event.date && $event.date.day !== 0 ? $event.date : this.fromDate;
        let date = moment($event.jsdate);
        let formatedDate = this.formatDateTime(date);
        if (!formatedDate || formatedDate === this.dateRangeValue.dateBegin) { return; }
        this.dateRangeValue.dateBegin = formatedDate;
        if (this.disableToDate) {
            this.applyDateChanges();
        }

        this.setKeyDownValues(formatedDate, this.keyDownValue, this.isSelectionExists);

        this.isDateChanged = true;
    }

    public onDateEndChange($event: IMyDateModel) {
        this.toDate = $event.date && $event.date.day !== 0 ? $event.date : this.toDate;
        let date = moment($event.jsdate);
        let formatedDate = this.formatDateTime(date);
        if (!formatedDate || formatedDate === this.dateRangeValue.dateEnd) { return; }
        this.dateRangeValue.dateEnd = formatedDate;

        this.setKeyDownValues(formatedDate, this.keyDownValue, this.isSelectionExists);

        this.isDateChanged = true;
    }

    public onKeyDown(event: KeyboardEvent): void {
        let eventTarget = (event.target as HTMLInputElement);
        this.isKeyDown = this.isKeyDown ? false : this.isKeyDown;
        this.isSelectionExists = this.isSelectionExists ? false : this.isSelectionExists;
        this.keyDownValue = eventTarget.value;
        this.currCursorPosition = (event.key.toLowerCase() === 'backspace' ? eventTarget.selectionStart - 1 : eventTarget.selectionStart);
        this.isSelectionExists = (eventTarget.selectionEnd > eventTarget.selectionStart ? true : false);
    }

    public onKeyPress(event: { target: HTMLInputElement; }): void {
        this.currCursorPosition = event.target.selectionStart + 1;
    }

    public onInput(event: { target: HTMLInputElement; }): void {
        let position = event.target.selectionStart;
        let dateEle: string[] = event.target.value.split('-');

        if (this.isKeyDown && this.isDateChanged) {
            event.target.value = this.keyDownValue;
            event.target.selectionEnd = (this.currCursorPosition < 0 ? 0 : this.currCursorPosition);
            this.keyDownValue = '';
            this.isKeyDown = false;
            this.isDateChanged = false;
        } else {
            if (dateEle.length > 0) {
                if (dateEle[0].length === 1) {
                    this.isDayChanged = true;
                }
            }

            if (dateEle.length > 1) {
                if (dateEle[1].length !== 3) {
                    this.isMonthChanged = true;
                }
            }

            if (dateEle.length > 2) {
                if (dateEle[2].length !== 4) {
                    this.isYearChanged = true;
                }
            }

            if (this.isDayChanged) {
                event.target.selectionEnd = (position === 0 ? 0 : 1);
                this.isDayChanged = false;
            } else {
                if (this.isDateChanged) {
                    if (this.currCursorPosition > 10) {
                        event.target.selectionEnd = this.currCursorPosition;
                        this.currCursorPosition = 0;
                    } else {
                        if (this.isYearChanged && !this.isMonthChanged) {
                            event.target.selectionEnd = position;
                            this.isYearChanged = false;
                        } else if (this.isMonthChanged) {
                            event.target.selectionEnd = (this.isMonthFormateManipulated ? this.currCursorPosition : position);
                            this.isMonthChanged = false;
                            this.isMonthFormateManipulated = false;
                        } else {
                            event.target.selectionEnd = (position < 2 ? position : 2);
                        }
                    }
                    this.isDateChanged = false;
                } else {
                    event.target.selectionEnd = (position === 0 ? 0 : position);
                }
            }
        }
    }

    public isInvalidDateRange() {
        if (!this.dateRangeValue) {
            return '';
        }

        const dateBegin = moment(this.dateRangeValue.dateBegin);
        const dateEnd = moment(this.dateRangeValue.dateEnd);

        if (!dateBegin.isValid || !dateEnd.isValid) {
            return '';
        }

        var error = '';

        if (dateBegin.isAfter(dateEnd)) {
            error = 'From date should not be greater than To date.';
        }

        if (dateEnd.isAfter(moment())) {
            error = error + ' To date should not be greater than current date.';
        }

        if (dateBegin.isAfter(moment())) {
            error = error + ' From date should not be greater than current date.';
        }

        if (error.length > 0) {
            this.isDateChanged = false;
        }

        return error;
    }

    public getCustomRangerLabel() {
        if (!this.dateRangeValue) {
            return this.customRangeKind.value;
        }
        let value = '';
        if (this.dateRangeValue.dateBegin === '') {
            value = this.customFromDateLabel;
        } else {
            const dateBegin = moment(this.dateRangeValue.dateBegin);
            value = this.formatDateTime(dateBegin, this.myDatePickerOptions.dateFormat.toUpperCase(), this.customFromDateLabel);
        }

        if (this.dateRangeValue.dateEnd === '' || this.disableToDate) {
            value = value + ' - ' + this.customToDateLabel;
        } else {
            const dateEnd = moment(this.dateRangeValue.dateEnd);
            value = value + ' - ' + this.formatDateTime(dateEnd, this.myDatePickerOptions.dateFormat.toUpperCase(), this.customToDateLabel);
        }
        return value;
    }

    public onPopoverHide() {
        this.showDropDown = false;
    }

    public getTodayRange(): DateRangeFilterValue {
        return {
            dateBegin: this.formatDateTime(moment()),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'today'
        };
    }

    public getWeeklyRange(): DateRangeFilterValue {
        return {
            dateBegin: this.formatDateTime(moment().subtract(7, 'd')),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'weekly'
        };
    }

    public getMonthlyRange(): DateRangeFilterValue {
        return {
            dateBegin: this.formatDateTime(moment().subtract(30, 'd')),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'monthly'
        };
    }

    public getTwoMonthsRange(): DateRangeFilterValue {
        return {
            dateBegin: this.formatDateTime(moment().subtract(60, 'd')),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'twoMonths'
        };
    }

    public getQuarterlyRange(): DateRangeFilterValue {
        return {
            dateBegin: this.formatDateTime(moment().subtract(90, 'd')),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'quarterly'
        };
    }

    private setKeyDownValues(formatedDateValue: string, keyDownDateValue: string, isSelection: boolean) {
        let formatedDateEle: string[] = formatedDateValue.split('-');

        if (keyDownDateValue !== '') {
            let keyDownEle: string[] = keyDownDateValue.split('-');
            if ((keyDownEle[0].indexOf('0') !== formatedDateEle[2].indexOf('0')) && keyDownEle.length >= 3) {
                this.isKeyDown = true;
                this.keyDownValue = ((keyDownEle[0].length < 2 && keyDownEle[0] !== '') ? formatedDateEle[2] : formatedDateEle[2].replace('0', '')) + '-' + keyDownEle[1] + '-' + formatedDateEle[0];
            }

            if (isSelection) {
                this.isKeyDown = true;
                this.keyDownValue = formatedDateEle[2].replace('0', '') + '-' + keyDownEle[1] + '-' + formatedDateEle[0];
                this.isSelectionExists = false;
            }

            if ((keyDownEle[1].charAt(0) !== keyDownEle[1].charAt(0).toUpperCase()) || (keyDownEle[1].charAt(1) !== keyDownEle[1].charAt(1).toLowerCase())) {
                this.isMonthFormateManipulated = true;
            }
        }
    }

    private formatDateTime(value: moment.Moment, format: string = 'YYYY-MM-DD', defaultString: string = ''): string {
        return moment.isMoment(value) && moment(value).isValid() ? value.format(format) : defaultString;
    }

    private getDateRangeType(): DateRangeFilterValue {
        let defaultFromDate = {
            year: this.fromDate.year,
            month: this.fromDate.month - 1,
            day: this.fromDate.day
        };

        let defaultToDate = {
            year: this.toDate.year,
            month: this.toDate.month - 1,
            day: this.toDate.day
        };
        return {
            dateBegin: moment(defaultFromDate) ? this.formatDateTime(moment(defaultFromDate)) : '',
            dateEnd: moment(defaultToDate) ? this.formatDateTime(moment(defaultToDate)) : '',
            rangeType: 'dateRange'
        };
    }

    private getEmptyDateRangeValue(): DateRangeFilterValue {
        return {
            dateBegin: '',
            dateEnd: '',
            rangeType: 'dateRange'
        };
    }
}
